const Scale = {
  100: '100',
  120: '120',
  150: '150',

  DEFAULT: 100,
};
const TimeOff = {
  30000: '30000',
  60000: '60000',
  120000: '120000',
  300000: '300000',
  600000: '600000',
  1800000: '1800000',

  DEFAULT: 30000,
};

const ScreenData = class {
  static MIN_BRIGHTNESS = 0;
  static MAX_BRIGHTNESS = 255;
  static MIN_AUTO_BRIGHTNESS = -100;
  static MAX_AUTO_BRIGHTNESS = 100;
  static AUTO_BRIGHTNESS_MULTIPLY = 100;

  constructor(data = {}) {
    this.data = data;
  }

  get screen() {
    if (!this.autoBrightness) return this.screenBrightness;
    return this.screenAutoBrightness;
  }

  set screen(val) {
    if (!this.autoBrightness) this.screenBrightness = val;
    else this.screenAutoBrightness = val;
  }

  get min() {
    if (!this.autoBrightness) return this.minScreenBrightness;
    return this.minScreenAutoBrightness;
  }

  get max() {
    if (!this.autoBrightness) return this.maxScreenBrightness;
    return this.maxScreenAutoBrightness;
  }

  get screenBrightness() {
    return 'screenBrightness' in this.data ? this.data.screenBrightness.current : ScreenData.MIN_BRIGHTNESS;
  }

  set screenBrightness(val) {
    if (!('screenBrightness' in this.data)) this.data.screenBrightness = {};
    this.data.screenBrightness.current = val;
  }

  get screenAutoBrightness() {
    const value = 'screenAutoBrightness' in this.data ? this.data.screenAutoBrightness.current : this.minScreenAutoBrightness;
    return value * ScreenData.AUTO_BRIGHTNESS_MULTIPLY;
  }

  set screenAutoBrightness(val) {
    if (!('screenAutoBrightness' in this.data)) this.data.screenAutoBrightness = {};
    this.data.screenAutoBrightness.current = val / ScreenData.AUTO_BRIGHTNESS_MULTIPLY;
  }

  get minScreenBrightness() {
    if ('screenBrightness' in this.data) {
      if ('min' in this.data.screenBrightness) return this.data.screenBrightness.min;
    }
    return ScreenData.MIN_BRIGHTNESS;
  }

  get minScreenAutoBrightness() {
    let value = ScreenData.MIN_AUTO_BRIGHTNESS;
    if ('screenAutoBrightness' in this.data) {
      if ('min' in this.data.screenAutoBrightness) value = this.data.screenAutoBrightness.min;
    }
    return value * ScreenData.AUTO_BRIGHTNESS_MULTIPLY;
  }

  get maxScreenBrightness() {
    if ('screenBrightness' in this.data) {
      if ('max' in this.data.screenBrightness) return this.data.screenBrightness.max;
    }
    return ScreenData.MAX_BRIGHTNESS;
  }

  get maxScreenAutoBrightness() {
    let value = ScreenData.MAX_AUTO_BRIGHTNESS;
    if ('screenAutoBrightness' in this.data) {
      if ('max' in this.data.screenAutoBrightness) value = this.data.screenAutoBrightness.max;
    }
    return value * ScreenData.AUTO_BRIGHTNESS_MULTIPLY;
  }

  get autoBrightness() {
    return 'autoBrightness' in this.data ? this.data.autoBrightness : false;
  }

  set autoBrightness(val) {
    this.data.autoBrightness = val;
  }

  get autoScreenRotate() {
    return 'autoScreenRotate' in this.data ? this.data.autoScreenRotate : false;
  }

  set autoScreenRotate(val) {
    this.data.autoScreenRotate = val;
  }

  get screenScale() {
    return 'screenScale' in this.data ? this.data.screenScale : Scale.DEFAULT;
  }

  set screenScale(val) {
    this.data.screenScale = val;
  }

  get screenTimeOff() {
    return 'screenTimeOff' in this.data ? this.data.screenTimeOff : TimeOff.DEFAULT;
  }

  set screenTimeOff(val) {
    this.data.screenTimeOff = val;
  }

  get toFB() {
    return {
      screenBrightness: {
        min: this.minScreenBrightness,
        max: this.maxScreenBrightness,
        current: this.screenBrightness,
      },
      screenAutoBrightness: {
        min: this.minScreenAutoBrightness / ScreenData.AUTO_BRIGHTNESS_MULTIPLY,
        max: this.maxScreenAutoBrightness / ScreenData.AUTO_BRIGHTNESS_MULTIPLY,
        current: this.screenAutoBrightness / ScreenData.AUTO_BRIGHTNESS_MULTIPLY,
      },
      autoBrightness: this.autoBrightness,
      autoScreenRotate: this.autoScreenRotate,
      screenTimeOff: this.screenTimeOff,
      screenScale: this.screenScale,
    };
  }

  static isValid(data) {
    const keys = [
      'screenBrightness',
      'autoBrightness',
      'autoScreenRotate',
      'screenTimeOff',
      'screenScale',
    ];
    if (data) return keys.every(item => item in data);
    return false;
  }
};

const SoundData = class {
  static MIN_VOLUME = 0;
  static MAX_VOLUME = 15;

  constructor(data = {}) {
    this.data = data;
  }

  get ringingVolume() {
    const val = 'ringingVolume' in this.data ? this.data.ringingVolume.current : SoundData.MIN_VOLUME;
    return val === 0 ? 1 : val;
  }

  set ringingVolume(val) {
    if (!('ringingVolume' in this.data)) this.data.ringingVolume = {};
    this.data.ringingVolume.current = val;
  }

  get minRingingVolume() {
    if ('ringingVolume' in this.data) {
      if ('min' in this.data.ringingVolume) return this.data.ringingVolume.min;
    }
    return SoundData.MIN_VOLUME;
  }

  get maxRingingVolume() {
    if ('ringingVolume' in this.data) {
      if ('max' in this.data.ringingVolume) return this.data.ringingVolume.max;
    }
    return SoundData.MAX_VOLUME;
  }

  get mediaVolume() {
    return 'mediaVolume' in this.data ? this.data.mediaVolume.current : SoundData.MIN_VOLUME;
  }

  set mediaVolume(val) {
    if (!('mediaVolume' in this.data)) this.data.mediaVolume = {};
    this.data.mediaVolume.current = val;
  }

  get minMediaVolume() {
    if ('mediaVolume' in this.data) {
      if ('min' in this.data.mediaVolume) return this.data.mediaVolume.min;
    }
    return SoundData.MIN_VOLUME;
  }

  get maxMediaVolume() {
    if ('mediaVolume' in this.data) {
      if ('max' in this.data.mediaVolume) return this.data.mediaVolume.max;
    }
    return SoundData.MAX_VOLUME;
  }

  get alarmVolume() {
    return 'alarmVolume' in this.data ? this.data.alarmVolume.current : SoundData.MIN_VOLUME;
  }

  set alarmVolume(val) {
    if (!('alarmVolume' in this.data)) this.data.alarmVolume = {};
    this.data.alarmVolume.current = val;
  }

  get minAlarmVolume() {
    if ('alarmVolume' in this.data) {
      if ('min' in this.data.alarmVolume) return this.data.alarmVolume.min;
    }
    return SoundData.MIN_VOLUME;
  }

  get maxAlarmVolume() {
    if ('alarmVolume' in this.data) {
      if ('max' in this.data.alarmVolume) return this.data.alarmVolume.max;
    }
    return SoundData.MAX_VOLUME;
  }

  get callVolume() {
    return 'callVolume' in this.data ? this.data.callVolume.current : SoundData.MIN_VOLUME;
  }

  set callVolume(val) {
    if (!('callVolume' in this.data)) this.data.callVolume = {};
    this.data.callVolume.current = val;
  }

  get minCallVolume() {
    if ('callVolume' in this.data) {
      if ('min' in this.data.callVolume) return this.data.callVolume.min;
    }
    return SoundData.MIN_VOLUME;
  }

  get maxCallVolume() {
    if ('callVolume' in this.data) {
      if ('max' in this.data.callVolume) return this.data.callVolume.max;
    }
    return SoundData.MAX_VOLUME;
  }

  get ringVibration() {
    return 'ringVibration' in this.data ? this.data.ringVibration : false;
  }

  set ringVibration(val) {
    this.data.ringVibration = val;
  }

  get vibrationOnTouch() {
    return 'vibrationOnTouch' in this.data ? this.data.vibrationOnTouch : false;
  }

  set vibrationOnTouch(val) {
    this.data.vibrationOnTouch = val;
  }

  get doNotDisturb() {
    return 'doNotDisturbMode' in this.data ? this.data.doNotDisturbMode !== 3 : false;
  }

  get toFB() {
    return {
      alarmVolume: {
        min: this.minAlarmVolume,
        max: this.maxAlarmVolume,
        current: this.alarmVolume,
      },
      callVolume: {
        min: this.minCallVolume,
        max: this.maxCallVolume,
        current: this.callVolume,
      },
      mediaVolume: {
        min: this.minMediaVolume,
        max: this.maxMediaVolume,
        current: this.mediaVolume,
      },
      ringVibration: this.ringVibration,
      ringingVolume: {
        min: this.minRingingVolume,
        max: this.maxRingingVolume,
        current: this.ringingVolume,
      },
      vibrationOnTouch: this.vibrationOnTouch,
    };
  }

  static isValid(data) {
    const keys = [
      'alarmVolume',
      'callVolume',
      'mediaVolume',
      'ringVibration',
      'ringingVolume',
      'vibrationOnTouch',
    ];
    if (data) return keys.every(item => item in data);
    return false;
  }
};

export {
  ScreenData,
  Scale,
  TimeOff,
  SoundData,
};



// WEBPACK FOOTER //
// ./src/store/models/settings.js